import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import {
  bookSetupCalendarAppointment,
  fetchSetupCalendarAvailableDates,
  fetchSetupCalendarFreeSlots,
} from "../../../api/customerSetup";

const BOOKING_WITH_DATA_WIDGET_URL =
  process.env.REACT_APP_BOOKING_WITH_DATA_WIDGET_URL || "";
const BOOKING_WITHOUT_DATA_WIDGET_URL =
  process.env.REACT_APP_BOOKING_WITHOUT_DATA_WIDGET_URL || "";

const getCalendarIdFromWidgetUrl = (widgetUrl) => {
  const match = widgetUrl.match(/\/widget\/booking[s]?\/([^/?#]+)/i);
  return match?.[1] || "";
};

const getBookingWidgetUrl = () => {
  const skippedBankSetup =
    localStorage.getItem("skipped_bank_setup") === "true";
  const hasPartialBankLinking =
    localStorage.getItem("partial_bank_linking") === "true";

  return skippedBankSetup || hasPartialBankLinking
    ? BOOKING_WITHOUT_DATA_WIDGET_URL
    : BOOKING_WITH_DATA_WIDGET_URL;
};

const getCalendarId = () => {
  const widgetUrl = getBookingWidgetUrl();
  return getCalendarIdFromWidgetUrl(widgetUrl);
};

const getBookingType = () => {
  const skippedBankSetup =
    localStorage.getItem("skipped_bank_setup") === "true";
  const hasPartialBankLinking =
    localStorage.getItem("partial_bank_linking") === "true";
  return skippedBankSetup || hasPartialBankLinking ? "without_data" : "with_data";
};

const extractApiErrorMessage = (err) => {
  const data = err?.response?.data;
  if (!data) return null;

  // Token / auth errors
  if (data.code === "token_not_valid" || err?.response?.status === 401) {
    return "Your session has expired. Please refresh the page and try again.";
  }

  // Standard DRF detail string
  if (typeof data.detail === "string") return data.detail;

  // Field-level or generic error string
  if (typeof data.error === "string") return data.error;

  // Array of message objects (DRF JWT messages list)
  if (Array.isArray(data.messages) && data.messages[0]?.message) {
    return data.messages[0].message;
  }

  return null;
};
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const COMMON_TIMEZONES = [
  "Africa/Johannesburg",
  "UTC",
  "Europe/London",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Australia/Sydney",
];

const toDateInput = (date) => date.toISOString().slice(0, 10);

const isSameDayKey = (dayValue, dateKey) => {
  if (!dayValue || !dateKey) return false;
  return dayValue.startOf("day").format("YYYY-MM-DD") === dateKey;
};

const formatSlotLabel = (slotIso, timezone) => {
  const slotDate = new Date(slotIso);
  return slotDate.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  });
};



export const BookACall = forwardRef(({ onBookingSuccess, profile }, ref) => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(BROWSER_TIMEZONE);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isInitializingCalendar, setIsInitializingCalendar] = useState(false);
  const [slotLoadError, setSlotLoadError] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [contact, setContact] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  const [availableDateKeys, setAvailableDateKeys] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(dayjs());
  const [loadingAvailableDates, setLoadingAvailableDates] = useState(false);

  const timezoneOptions = useMemo(() => {
    const merged = Array.from(
      new Set([BROWSER_TIMEZONE, ...COMMON_TIMEZONES, selectedTimezone])
    );
    return merged.filter(Boolean);
  }, [selectedTimezone]);

  useEffect(() => {
    const user = profile?.user || {};
    setContact((prev) => ({
      firstName: prev.firstName || user.first_name || "",
      lastName: prev.lastName || user.last_name || "",
      email: prev.email || user.email || "",
      phoneNumber: prev.phoneNumber || profile?.phone_number || user.phone_number || "",
    }));
  }, [profile]);

  const isSameOrAfterDay = (dateValue, compareValue) =>
    !dayjs(dateValue).startOf("day").isBefore(dayjs(compareValue).startOf("day"));

  useEffect(() => {
    if (!selectedDate) {
      setSlots([]);
      setSelectedSlot("");
      setLoadingSlots(false);
      return;
    }

    let isCancelled = false;

    const loadSlots = async () => {
      setLoadingSlots(true);
      setSlotLoadError("");
      setSelectedSlot("");
      try {
        const calendarId = getCalendarId();
        const bookingType = getBookingType();
        const response = await fetchSetupCalendarFreeSlots({
          date: selectedDate,
          timezone: selectedTimezone,
          bookingType,
          ...(calendarId ? { calendarId } : {}),
        });
        if (!isCancelled) {
          setSlots(Array.isArray(response?.slots) ? response.slots : []);
        }
      } catch (err) {
        if (!isCancelled) {
          setSlots([]);
          setSlotLoadError(
            extractApiErrorMessage(err) ||
              "Could not load available slots for this date."
          );
        }
      } finally {
        if (!isCancelled) {
          setLoadingSlots(false);
        }
      }
    };

    loadSlots();
    return () => {
      isCancelled = true;
    };
  }, [selectedDate, selectedTimezone]);

  useEffect(() => {
    let isCancelled = false;

    const initializeCalendar = async () => {
      setIsInitializingCalendar(true);
      try {
        const calendarId = getCalendarId();
        const bookingType = getBookingType();
        const monthValue = dayjs().format("YYYY-MM");
        const response = await fetchSetupCalendarAvailableDates({
          month: monthValue,
          timezone: selectedTimezone,
          bookingType,
          ...(calendarId ? { calendarId } : {}),
        });

        if (isCancelled) return;

        const availableDates = Array.isArray(response?.available_dates)
          ? response.available_dates
          : [];

        setAvailableDateKeys(availableDates);

        const initialSelectedDate =
          availableDates.find((dateKey) => isSameOrAfterDay(dateKey, dayjs())) ||
          availableDates[0] ||
          "";

        if (initialSelectedDate) {
          setSelectedDate(initialSelectedDate);
          setCalendarMonth(dayjs(initialSelectedDate).startOf("month"));
        } else {
          setSelectedDate("");
          setCalendarMonth(dayjs().startOf("month"));
        }

        setSlots([]);
        setSelectedSlot("");
      } catch (err) {
        if (!isCancelled) {
          setAvailableDateKeys([]);
          setSelectedDate("");
          setSlots([]);
          setSelectedSlot("");
          setCalendarMonth(dayjs().startOf("month"));
        }
      } finally {
        if (!isCancelled) {
          setIsInitializingCalendar(false);
        }
      }
    };

    initializeCalendar();
    return () => {
      isCancelled = true;
    };
  }, [selectedTimezone]);

  const handleContactChange = (field, value) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  useImperativeHandle(ref, () => ({
    submitBooking: async () => {
      const nextErrors = {};
      setBookingError("");

      if (!contact.firstName?.trim()) nextErrors.firstName = "First name is required.";
      if (!contact.lastName?.trim()) nextErrors.lastName = "Last name is required.";
      if (!contact.email?.trim()) nextErrors.email = "Email is required.";
      if (!selectedSlot) nextErrors.selectedSlot = "Please select an available time slot.";

      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        throw new Error("Missing booking details.");
      }

      setFieldErrors({});
      setBookingError("");

      try {
        const calendarId = getCalendarId();
        const bookingType = getBookingType();
        await bookSetupCalendarAppointment({
          ...contact,
          selectedSlot,
          timezone: selectedTimezone,
          bookingType,
          ...(calendarId ? { calendarId } : {}),
        });
      } catch (err) {
        const message =
          extractApiErrorMessage(err) || "Could not book the appointment. Please try again.";
        setBookingError(message);
        throw err;
      }

      onBookingSuccess?.();
      return true;
    },
  }));

  const showSelectedSlotError = Boolean(fieldErrors.selectedSlot);

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Stack spacing={2.5}>
        {isInitializingCalendar && !selectedDate ? (
          <Box
            sx={{
              minHeight: 360,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Box sx={{ width: { xs: "100%", md: 360 }, flexShrink: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Select date
              </Typography>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  referenceDate={calendarMonth}
                  value={calendarMonth}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    const nextDay = newValue.startOf("day");
                    setCalendarMonth(nextDay.startOf("month"));
                    setSelectedDate(nextDay.format("YYYY-MM-DD"));
                  }}
                  onMonthChange={async (newMonth) => {
                    if (!newMonth) return;
                    const nextMonth = newMonth.startOf("month");
                    setCalendarMonth(nextMonth);
                    setLoadingAvailableDates(true);
                    try {
                      const calendarId = getCalendarId();
                      const bookingType = getBookingType();
                      const response = await fetchSetupCalendarAvailableDates({
                        month: nextMonth.format("YYYY-MM"),
                        timezone: selectedTimezone,
                        bookingType,
                        ...(calendarId ? { calendarId } : {}),
                      });
                      const availableDates = Array.isArray(response?.available_dates)
                        ? response.available_dates
                        : [];
                      setAvailableDateKeys(availableDates);
                    } catch {
                      // Keep existing keys on error — don't clear what's already shown.
                    } finally {
                      setLoadingAvailableDates(false);
                    }
                  }}
                  disablePast
                  slots={{
                    day: (dayProps) => {
                      const dayKey = dayProps.day.format("YYYY-MM-DD");
                      const isAvailable = availableDateKeys.includes(dayKey);
                      const isSelected = isSameDayKey(dayProps.day, selectedDate);

                      return (
                        <PickersDay
                          {...dayProps}
                          selected={isSelected}
                          sx={{
                            ...(dayProps.sx || {}),
                            ...(isAvailable && !isSelected
                              ? {
                                  backgroundColor: "rgba(83, 109, 236, 0.12)",
                                  color: "#2563EB",
                                  "&:hover": {
                                    backgroundColor: "rgba(83, 109, 236, 0.18)",
                                  },
                                }
                              : {}),
                          }}
                        />
                      );
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                select
                label="Timezone"
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                fullWidth
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                {timezoneOptions.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Available slots
              </Typography>

              {loadingSlots ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {slots.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No slots available for the selected day.
                    </Typography>
                  ) : (
                    slots.map((slot) => (
                      <Button
                        key={slot}
                        variant={selectedSlot === slot ? "contained" : "outlined"}
                        onClick={() => {
                          setSelectedSlot(slot);
                          setFieldErrors((prev) => ({ ...prev, selectedSlot: "" }));
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        {formatSlotLabel(slot, selectedTimezone)}
                      </Button>
                    ))
                  )}
                </Stack>
              )}

              {slotLoadError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {slotLoadError}
                </Alert>
              )}
              {showSelectedSlotError && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1, display: "block" }}>
                  {fieldErrors.selectedSlot}
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            label="First Name"
            value={contact.firstName}
            onChange={(e) => handleContactChange("firstName", e.target.value)}
            error={Boolean(fieldErrors.firstName)}
            helperText={fieldErrors.firstName || ""}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={contact.lastName}
            onChange={(e) => handleContactChange("lastName", e.target.value)}
            error={Boolean(fieldErrors.lastName)}
            helperText={fieldErrors.lastName || ""}
            fullWidth
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            label="Email"
            type="email"
            value={contact.email}
            onChange={(e) => handleContactChange("email", e.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email || ""}
            fullWidth
          />
          <TextField
            label="Phone Number"
            value={contact.phoneNumber}
            onChange={(e) => handleContactChange("phoneNumber", e.target.value)}
            fullWidth
          />
        </Stack>

        {bookingError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {bookingError}
          </Alert>
        )}
      </Stack>
    </Box>
  );
});
